import { UserRole } from "constants/userRole";
import { useMemo } from "react";
import { useGetAccountByIdQuery } from "store/queries/account";

import { useCurrentUser } from "./useCurrentUser";

/**
 * CLE-2811 / CLE-2851 — the rule behind useContractManagementBot, pure so it can be tested directly.
 *
 * The account must have a Contract Management bot assigned (the "Contract Management Chat Bot Enabled"
 * switch on the hospital's edit page), AND the viewer must be a super admin OR the account must have
 * "Hospital Staff Access" (`features.contractChatbotStaffEnabled`) on. The bot is required either way:
 * the switch alone never shows the widget — the API route guard applies the same rule, so a widget
 * shown here is one whose messages are accepted.
 */
export function canUseContractManagementBot(
  account:
    | {
        contractManagementChatBotId?: number | null;
        features?: { contractChatbotStaffEnabled?: boolean } | null;
      }
    | null
    | undefined,
  userScope: string | null | undefined
): boolean {
  if (!account?.contractManagementChatBotId) return false;
  if (userScope === UserRole.SUPER_ADMIN) return true;
  return account.features?.contractChatbotStaffEnabled === true;
}

/**
 * CLE-2811 — whether the Contract Management chat widget should be shown for an account.
 *
 * Extracted from FeeScheduleComparison so the Library tab and the contract page gate identically —
 * three copies of this condition is how one of them ends up wrong. See canUseContractManagementBot.
 */
export function useContractManagementBot(accountId?: string): boolean {
  const { data: accountResponse } = useGetAccountByIdQuery(accountId!, {
    skip: !accountId,
  });
  const { userInfo } = useCurrentUser({ skip: false });

  const botId = accountResponse?.data?.contractManagementChatBotId;
  const staffEnabled =
    accountResponse?.data?.features?.contractChatbotStaffEnabled;

  return useMemo(
    () =>
      canUseContractManagementBot(
        {
          contractManagementChatBotId: botId,
          features: { contractChatbotStaffEnabled: staffEnabled },
        },
        userInfo?.scope
      ),
    [botId, staffEnabled, userInfo?.scope]
  );
}

export default useContractManagementBot;
